import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  SearchOffOutlined as NotFoundIcon,
  RefreshRounded as RefreshIcon,
  CheckCircle as CheckIcon,
  ScheduleOutlined as ClockIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';

// การ์ด "payment ในระบบที่ไม่พบในธนาคาร" หน้า company-account — payment ในระบบ (SUCCESS) ที่ยัง
// ไม่ยืนยัน OCR ตั้งแต่วันกำหนดยอดตั้งต้น ให้ฝ่ายการเงินจัดการรายตัว:
//  - "ยืนยันด้วยตนเอง" (เช็คทางอื่นแล้วว่าเงินออก/เข้าจริง) → สมุดขึ้น "ยืนยันแล้ว" ออกจากลิสต์นี้
//  - "ค้างที่ธนาคาร" (เงินยังไม่ตัดจริง ลงวันเร็วไป) → ติดธง รอรูปรอบหน้ามาเคลียร์ (toggle ได้)
const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('th-TH');
};

export function NotFoundPaymentsCard({ customerId, dateFrom }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  // dialog สำหรับกรอกหมายเหตุ — mode: 'verify' (บังคับกรอก) | 'outstanding' (ไม่บังคับ)
  const [dlg, setDlg] = useState(null); // { mode, row } | null
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listNotFoundPayments({ customerId, dateFrom }));
      setRows(data.rows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dispatch, customerId, dateFrom]);

  useEffect(() => {
    load();
  }, [load]);

  const openDlg = (mode, row) => {
    setNote(mode === 'outstanding' ? (row.recon?.outstanding_note || '') : '');
    setDlg({ mode, row });
  };

  const closeDlg = () => setDlg(null);

  const submitDlg = async () => {
    const { mode, row } = dlg;
    if (mode === 'verify' && !note.trim()) return;

    setBusyId(row.id);
    setDlg(null);
    try {
      if (mode === 'verify') {
        await dispatch(actions.manualVerifyPayment({ id: row.id, note: note.trim() }));
      } else {
        await dispatch(actions.markPaymentOutstanding({ id: row.id, outstanding: true, note: note.trim() }));
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const clearOutstanding = async (row) => {
    setBusyId(row.id);
    try {
      await dispatch(actions.markPaymentOutstanding({ id: row.id, outstanding: false }));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: '#e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: '1px solid', borderColor: '#e2e8f0' }}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fff7ed', color: '#c2410c' }}>
              <NotFoundIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 750 }}>payment ในระบบที่ไม่พบในธนาคาร</Typography>
              <Typography variant="body2" color="text.secondary">
                รายการที่ยังไม่ยืนยัน OCR — ยืนยันด้วยตนเอง หรือทำเครื่องหมายว่าเงินยังไม่ตัดจากธนาคาร
              </Typography>
            </Box>
          </Stack>
          <Tooltip title="โหลดใหม่">
            <span>
              <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid', borderColor: '#e2e8f0' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ minHeight: 140, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={26} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>ไม่มีรายการค้าง</Typography>
          <Typography variant="body2" color="text.secondary">payment ทุกใบตั้งแต่วันกำหนดยอดตั้งต้นยืนยัน OCR แล้ว</Typography>
        </Box>
      ) : (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack spacing={1}>
            {rows.map((row) => {
              const outstanding = Boolean(row.recon?.outstanding);
              return (
                <Paper
                  key={row.id}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, borderColor: outstanding ? '#bfdbfe' : '#e2e8f0', bgcolor: outstanding ? '#eff6ff' : '#fff' }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          color={row.direction === 'income' ? 'success' : 'error'}
                          label={row.direction === 'income' ? 'เงินเข้า' : 'เงินออก'}
                          sx={{ height: 22 }}
                        />
                        <Typography sx={{ fontWeight: 800 }}>{formatCurrency(row.amount)} บาท</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.payment_number} · {fmtDate(row.date)}
                        </Typography>
                        {outstanding && (
                          <Chip size="small" color="primary" variant="outlined" icon={<ClockIcon />} label="ค้างที่ธนาคาร" sx={{ height: 22 }} />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                        {row.name || '(ไม่มีชื่อรายการ)'}
                        {outstanding && row.recon?.outstanding_note ? ` — ${row.recon.outstanding_note}` : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        disabled={busyId === row.id}
                        onClick={() => openDlg('verify', row)}
                      >
                        ยืนยันด้วยตนเอง
                      </Button>
                      {outstanding ? (
                        <Button size="small" variant="outlined" disabled={busyId === row.id} onClick={() => clearOutstanding(row)}>
                          ยกเลิกค้าง
                        </Button>
                      ) : (
                        <Button size="small" variant="outlined" disabled={busyId === row.id} onClick={() => openDlg('outstanding', row)}>
                          ค้างที่ธนาคาร
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      <Dialog open={Boolean(dlg)} onClose={closeDlg} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dlg?.mode === 'verify' ? 'ยืนยันด้วยตนเอง' : 'ทำเครื่องหมายค้างที่ธนาคาร'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {dlg?.mode === 'verify'
              ? 'ระบุว่าตรวจสอบยอดนี้จากไหน (เช่น statement PDF วันที่ ...) — รายการจะถูกทำเครื่องหมายว่ายืนยันแล้ว'
              : 'บันทึกหมายเหตุ (ไม่บังคับ) — รายการจะยังไม่ถูกยืนยัน แต่ติดธงว่าเงินยังไม่ตัดจากธนาคาร'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            placeholder={dlg?.mode === 'verify' ? 'เช่น เช็คจาก statement PDF แล้ว เงินออกจริง 24/8' : 'เช่น รอธนาคารตัดพรุ่งนี้'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDlg}>ยกเลิก</Button>
          <Button variant="contained" onClick={submitDlg} disabled={dlg?.mode === 'verify' && !note.trim()}>
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default NotFoundPaymentsCard;
